import React, { useState, useMemo, useRef, useEffect } from 'react';
import { usePageMeta, useStructuredData } from '../lib/seo';
import { Link } from 'react-router-dom';
import CTABanner from '../components/CTABanner';
import {
  Plus, Minus, Search, ArrowRight,
} from 'lucide-react';
import { PRIVATE_INCLUDED_USPS, PRIVATE_UNLOCKED_USPS, PRIVATE_ASSISTANT_USPS } from '../data/privateUsps';

/* ═══════════════════════════════════════════════════════════════════════
 * FAQPage — strukturiert, verkaufspsychologisch, non-redundant.
 * Fokus:
 *   · 12 Inklusiv-Leistungen (ab Tag 1 aktiv) — jede in eigener Frage
 *   · 13 Assistent-Services (immer inklusive) — jeder in eigener Frage
 *   · 12 Reise-Vorteile (über FuelX-Hotels-Buchungen freischaltbar) — jede in eigener Frage
 * ═══════════════════════════════════════════════════════════════════════ */

const INKLUSIV_FRAGEN = PRIVATE_INCLUDED_USPS.map((usp) => ({
  q: `${usp.title}`,
  a: usp.info,
  maxEur: null,
  slug: usp.slug,
}));

const ASSISTENT_FRAGEN = PRIVATE_ASSISTANT_USPS.map((usp) => ({
  q: `${usp.title}`,
  a: usp.info,
  maxEur: null,
  slug: usp.slug,
}));

const REISE_FRAGEN = PRIVATE_UNLOCKED_USPS.map((usp) => ({
  q: `${usp.title}`,
  a: usp.maxEur ? `${usp.info} Höchstbetrag: bis zu ${usp.maxEur.toLocaleString('de-DE')} €.` : usp.info,
  maxEur: usp.maxEur,
  slug: usp.slug,
}));

const FAQS = [
  // ── Start: der Deal in 3 Sätzen ──────────────────────────────────────
  {
    id: 'deal',
    cat: 'Der Deal in 3 Sätzen',
    // Sales-Psychology: Klarheit + Loss-Aversion
    items: [
      {
        q: 'Was macht FuelX anders als eine normale Tankkarte?',
        a: 'Normale Tankkarten sparen 1 – 5 Cent pro Liter — mehr nicht. FuelX ist eine Mobilitätsmitgliedschaft: Jeder gefahrene Kilometer erzeugt FuelGood Guthaben, das du direkt für Hotelbuchungen über FuelX Hotels einlöst — oder als Aufladung auf deine Prepaid-Tankkarte überträgst. So senken aktive Kunden ihren effektiven Literpreis um Ø 0,40 € (Richtwert bei aktiver Nutzung von FuelX Angeboten). Dazu kommen 23 Leistungen rund ums Auto ab Tag 1 (u. a. TÜV- & Service-Erinnerungen, kilometerbasierter Wartungsplan, Gebrauchtwagen-Historien-Check, Werkstatt-Checks, digitaler Fahrzeugordner, Verkaufs-Service) und 14 weitere Vorteile, die du über qualifizierende FuelX-Hotels-Buchungen freischaltest — darunter die kostenlose Fehlerdiagnose und der Kasko-Selbstbehalt. Ein Jahresbeitrag — kein Setup, keine Transaktionsgebühr.',
      },
      {
        q: 'Lohnt sich FuelX für mich?',
        a: 'Faustregel: Wenn du ein Auto fährst und mindestens eine Reise im Jahr planst, holst du den Jahresbeitrag in der Regel mehrfach heraus. Beispiel bei 15.000 km: 139,90 € Jahresbeitrag, dafür 1.125 € FuelGood Guthaben — einlösbar mit 25–250 € pro Hotelbuchung. Wer gar nicht reist, nutzt trotzdem die 23 Inklusiv-Leistungen wie Fehlerdiagnose, Schadenfoto-Check, Pannen-Soforthilfe, Versicherungs- und Autokredit-Check. Am schnellsten siehst du es im Konfigurator: Kilometer einstellen, Ergebnis live ablesen.',
      },
      {
        q: 'Was bekomme ich für 99,90 €?',
        a: '23 Leistungen ab Tag 1: 10 Experten-Leistungen (u. a. Schadenfoto-Check, Pannen-Soforthilfe, Versicherungs- & Kredit-Check, Fahrzeugbewertung) und 13 digitale Assistent-Services (u. a. TÜV-Erinnerungen, Autokosten-Check, Fahrzeugordner, Unfall-Assistent). Dazu dein FuelGood Guthaben (bei 15.000 km sind das 1.125 € Kaufkraft): Du löst es direkt für Hotelbuchungen ein — oder überträgst es mit einer qualifizierenden FuelX-Hotels-Buchung auf deine Prepaid Card und zahlst damit KFZ-Kosten wie TÜV, Ölwechsel, Reifenwechsel, Kfz-Steuer oder deine Kfz-Prämie (bis zum jeweiligen Höchstbetrag).',
      },
      {
        q: 'Wer steht dahinter?',
        a: 'FUEL.X Mobility — Aggregator und Lead-Vermittler zwischen dir und geprüften Dienstleistern. Wir sind explizit kein Versicherer, keine Bank und kein Reiseveranstalter. Diese Neutralität ist unser wertvollstes Asset: Sie erlaubt uns, das jeweils beste Angebot zurückzuspielen.',
      },
    ],
  },

  // ── 12 Inklusiv-Leistungen ───────────────────────────────────────────
  {
    id: 'inklusiv',
    cat: 'Ab Tag 1 aktiv · ohne Freischaltung',
    subtitle: 'Experten-Hilfe von der Warnleuchte bis zum Autoverkauf — ohne Zusatzbedingungen, ohne Freischaltung.',
    items: INKLUSIV_FRAGEN,
  },

  // ── 13 Assistent-Services ────────────────────────────────────────────
  {
    id: 'assistent',
    cat: '13 Assistent-Services · immer inklusive',
    subtitle: 'Dein digitaler Beifahrer — denkt an Termine, prüft Kosten, ordnet Unterlagen und hilft im Ernstfall.',
    items: ASSISTENT_FRAGEN,
  },

  // ── 12 Reise-Vorteile ────────────────────────────────────────────────
  {
    id: 'reisen',
    cat: 'Über eine Reise freischalten',
    subtitle: 'Du reist sowieso — jede FuelX-Hotels-Buchung öffnet einen weiteren Kostenblock am Auto.',
    items: REISE_FRAGEN,
  },

  // ── FuelGood verstehen · Bedingungen ─────────────────────────────────
  {
    id: 'fuelgood',
    cat: 'FuelGood verstehen · Bedingungen',
    subtitle: 'Die Mechanik hinter deinem Guthaben — ehrlich erklärt, bevor du kaufst.',
    items: [
      {
        q: 'Wie entsteht mein FuelGood Guthaben?',
        a: 'Beim Kauf wird dein Jahres-Guthaben aus deiner angegebenen Fahrleistung berechnet (0,1 Punkte pro Kilometer · 1 Punkt = 0,75 €) und sofort deinem Wallet gutgeschrieben — bei 15.000 km sind das 1.125 € Kaufkraft. Es ist 12 Monate gültig und zweckgebunden: einlösbar in der Plattform, nicht als Bargeld.',
      },
      {
        q: 'Muss ich reisen, um FuelX zu nutzen?',
        a: 'Nein. 23 Leistungen sind ab Tag 1 aktiv — ganz ohne Reise: TÜV- & Service-Erinnerungen, Wartungsplan, Reparaturkosten-Check, Gebrauchtwagen-Historien-Check, Kfz-Versicherungs- und Autokredit-Vergleich, digitaler Fahrzeugordner, Verkaufs-Service und mehr. Reisen brauchst du nur für die 14 freischaltbaren Vorteile (z. B. TÜV, Kfz-Steuer, Reifen aus Guthaben zahlen, kostenlose Fehlerdiagnose, Kasko-Selbstbehalt) — dafür genügt eine qualifizierende FuelX-Hotels-Buchung, die du vermutlich sowieso geplant hättest.',
      },
      {
        q: 'Welche Vorteile sind sofort inklusive — und welche muss ich freischalten?',
        a: 'Sofort inklusive (23): alles, was Hilfe, Prüfung, Vergleich und Organisation betrifft — vom Schadenfoto-Check bis zum Fahrzeugordner, ohne Zusatzbedingung. Freischaltbar (14): die Kostenblöcke, bei denen FuelGood Guthaben über deine Prepaid-Tankkarte echte Rechnungen deckt (TÜV/HU, Öl, Reifen, Kfz-Steuer, Kfz-Prämie u. a.) — jeweils nach einer qualifizierenden FuelX-Hotels-Buchung und bis zum jeweiligen Höchstbetrag. Auf jeder Leistungsseite steht der Status klar dabei.',
      },
      {
        q: 'Kann ich mir das Guthaben auszahlen lassen?',
        a: 'Nein. FuelGood ist zweckgebundenes Guthaben und keine Barauszahlung. Du löst es bei Hotelbuchungen ein (25–250 € pro Buchung) oder überträgst es auf deine Prepaid-Tankkarte für KFZ-Services. Genau diese Zweckbindung macht den Vorteil überhaupt erst möglich.',
      },
      {
        q: 'Welche Hotels kann ich buchen?',
        a: 'Über 1,6 Mio Unterkünfte weltweit über FuelX Hotels — vom Stadthotel bis zum Resort. Du buchst zum marktüblichen Preis; dein FuelGood Guthaben wird direkt beim Buchen verrechnet. Für die Freischaltung der Reise-Vorteile zählt eine qualifizierende FuelX-Hotels-Buchung gemäß den Bedingungen.',
      },
      {
        q: 'Was bedeutet „bis zu"?',
        a: '„Bis zu" ist der Höchstbetrag, den dein Guthaben pro Kostenblock decken kann — z. B. bis 160 € TÜV/HU oder bis 500 € Reifen. Wie viel davon du ausschöpfst, hängt von deinem Guthaben-Stand, deinem Status und den Teilnahmebedingungen ab. Ein Rechtsanspruch auf den Höchstbetrag besteht nicht — dafür siehst du deinen Stand jederzeit live im Dashboard.',
      },
      {
        q: 'Ist FuelX eine Versicherung oder Bank?',
        a: 'Nein. FuelX ist ein Serviceanbieter und Lead-Vermittler — explizit kein Versicherer, keine Bank und kein Kreditgeber. Versicherungs- und Kreditangebote kommen immer direkt vom jeweiligen lizenzierten Anbieter. FuelX analysiert, vergleicht und vermittelt — du entscheidest.',
      },
      {
        q: 'Was passiert, wenn ich einen Vorteil nicht nutze?',
        a: 'Nichts Schlimmes — aber auch kein Ansparen: FuelGood Guthaben ist 12 Monate gültig, danach verfällt der nicht genutzte Teil. Wir erinnern dich rechtzeitig vor Ablauf per E-Mail und im Dashboard, damit nichts liegen bleibt.',
      },
      {
        q: 'Gibt es Mindestaufenthalte oder weitere Voraussetzungen?',
        a: 'Für das Einlösen bei normalen Hotelbuchungen: nein. Für qualifizierende FuelX-Hotels-Buchungen (die Reise-Vorteile freischalten) gelten die jeweils angegebenen Bedingungen — etwa Mindestbuchungswert oder Mindestaufenthalt, abhängig vom Angebot. Alle Voraussetzungen stehen vor der Buchung transparent am Angebot, nie nur im Kleingedruckten.',
      },
    ],
  },

  // ── Card, Kündigung, Sicherheit ───────────────────────────────────────
  {
    id: 'card',
    cat: 'Card, Kündigung, Sicherheit',
    items: [
      {
        q: 'Was kostet die Silver Card?',
        a: 'Der Preis richtet sich nach deiner Jahres-Fahrleistung — bis 5.000 km: 99,90 €, bis 10.000 km: 119,90 €, bis 15.000 km: 139,90 €, bis 20.000 km: 159,90 €, bis 30.000 km: 169,90 €, bis 40.000 km: 179,90 €. Kein Setup, keine Transaktionsgebühr.',
      },
      {
        q: 'Wie funktioniert die Gold Card für Unternehmen?',
        a: '49,90 € pro Fahrzeug pro Jahr. Master-Account mit DATEV-Export, jedes Fahrzeug bekommt eine eigene virtuelle Card. Ab 201 Fahrzeugen individuelles Angebot mit dediziertem Account-Management.',
      },
      {
        q: 'Was passiert mit meinen Daten?',
        a: 'Deine Daten bleiben in deinem FuelX-Account. Weitergegeben wird nur, was du ausdrücklich freigibst — z. B. für einen Kfz-Versicherungs-Check an den betreffenden Anbieter. Hosting in der EU, DSGVO-konform, keine Werbe-Weitergabe.',
      },
      {
        q: 'Kann ich meinen Vertrag jederzeit kündigen?',
        a: '14 Tage gesetzliches Widerrufsrecht mit voller Rückerstattung. Danach 30 Tage Frist zum Laufzeitende — formlos per E-Mail oder direkt im Dashboard.',
      },
      {
        q: 'Wie sicher ist die Bezahlung?',
        a: 'Payment über Stripe (PCI-DSS Level 1). 3-D-Secure, TLS-Verschlüsselung, tokenisierte Zahlungsmethoden. Kartendaten sehen wir zu keinem Zeitpunkt.',
      },
      {
        q: 'Ist FuelX seriös?',
        a: 'FuelX (fuelxcard.com) ist eine eingetragene Marke der FUEL.X Mobility mit Sitz in Waiblingen bei Stuttgart — mit deutschem Impressum, echtem Ansprechpartner (Inhaber: Ahmad Alemi), 14 Tagen gesetzlichem Widerrufsrecht, Zahlung über Stripe und DSGVO-konformem Hosting in der EU. FuelX ist kein Versicherer und keine Bank, sondern Plattform und Vermittler zwischen dir und geprüften Dienstleistern.',
      },
      {
        q: 'Wie erreiche ich den FuelX Kundenservice?',
        a: 'Am schnellsten per E-Mail an info@fuelxcard.com oder über das Kontaktformular auf fuelxcard.com/kontakt. Mitglieder erreichen ihren persönlichen Ansprechpartner zusätzlich direkt im Dashboard. Wir antworten werktags in der Regel innerhalb von 24 Stunden.',
      },
    ],
  },
];

/* ─── Highlight-Snippet für Suchtreffer ─────────────────────────────── */
function highlightText(text, query) {
  if (!query.trim()) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-emerald-100 text-emerald-900 rounded px-0.5">{p}</mark>
      : <React.Fragment key={i}>{p}</React.Fragment>,
  );
}

/* ─── FAQ Item ─────────────────────────────────────────────────────── */
function FAQItem({ q, a, open, toggle, query, itemMax }) {
  return (
    <div className="border-t border-stone-100 first:border-t-0">
      <button
        onClick={toggle}
        className="w-full flex items-start justify-between gap-6 py-5 sm:py-6 text-left group"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <span className={`text-[15px] sm:text-[17px] font-medium leading-snug tracking-[-0.005em] transition-colors ${open ? 'text-stone-900' : 'text-stone-800 group-hover:text-stone-900'}`}>
            {highlightText(q, query)}
          </span>
          {itemMax && (
            <span className="ml-2.5 inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 tabular-nums align-middle">
              bis {itemMax.toLocaleString('de-DE')} €
            </span>
          )}
        </div>
        <span className={`flex-shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180 text-emerald-500' : 'text-stone-400 group-hover:text-stone-900'}`}>
          {open ? <Minus className="w-4 h-4" strokeWidth={2} /> : <Plus className="w-4 h-4" strokeWidth={2} />}
        </span>
      </button>
      {open && (
        <div className="pb-6 pr-2 sm:pr-14 animate-in fade-in slide-in-from-top-1 duration-300">
          <p className="text-[14.5px] text-stone-500 leading-[1.75] max-w-2xl">
            {highlightText(a, query)}
          </p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * FAQPage
 * ═══════════════════════════════════════════════════════════════════════ */
export default function FAQPage() {
  usePageMeta('FuelX FAQ | Alle Leistungen & Fragen im Überblick', 'Alle Antworten zu FuelX: 23 Leistungen ab Tag 1, 14 über eine Reise freischaltbar — alle 37 einzeln erklärt — plus Kosten, FuelGood-Guthaben, Kündigung und Sicherheit.');

  // JSON-LD FAQPage-Schema für Google Rich Results (alle 57 Fragen & Antworten)
  const faqSchema = useMemo(() => ({
    '@type': 'FAQPage',
    mainEntity: FAQS.flatMap((c) => c.items).map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }), []);
  useStructuredData(faqSchema, 'faq-page');
  const [openIdx, setOpenIdx] = useState('deal-0');
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('deal');
  const sectionRefs = useRef({});

  const totalCount = useMemo(() => FAQS.reduce((n, c) => n + c.items.length, 0), []);

  const filteredFAQs = useMemo(() => {
    if (!query.trim()) return FAQS;
    const q = query.toLowerCase().trim();
    return FAQS.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [query]);

  const hitCount = useMemo(
    () => filteredFAQs.reduce((n, c) => n + c.items.length, 0),
    [filteredFAQs],
  );

  useEffect(() => {
    if (query.trim()) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveCat(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5] },
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [query]);

  const scrollToCategory = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white pt-[92px]" data-testid="faq-page">
      {/* ═══ HERO — minimalist, verkaufspsychologisch ═══ */}
      <section className="pt-16 pb-14 sm:pt-24 sm:pb-20 border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-400 mb-8">FAQ · {totalCount} Antworten</p>

          <h1 className="font-display text-[2.75rem] sm:text-[4.25rem] lg:text-[5.5rem] leading-[0.92] tracking-[-0.03em] text-stone-900 max-w-4xl">
            Erst verstehen.<br />
            <span className="text-emerald-500 italic font-light">Dann entscheiden.</span>
          </h1>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 md:items-end">
            <p className="text-[15px] sm:text-base text-stone-500 max-w-xl leading-[1.65]">
              FuelX ist mehr als eine Tankkarte — eine Mitgliedschaft rund um deine Autokosten: 23 Vorteile ab Tag 1, 14 weitere über eine Reise freischaltbar, die du sowieso planst. Hier stehen alle Bedingungen offen — bevor du zahlst.
            </p>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400 pointer-events-none" strokeWidth={2} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suchen — TÜV · Öl · Steuer …"
                className="w-full pl-10 pr-16 py-3 rounded-full border border-stone-200 bg-stone-50/50 text-[13px] placeholder:text-stone-400 focus:bg-white focus:border-stone-900 focus:outline-none focus:ring-4 focus:ring-emerald-50 transition-all"
                data-testid="faq-search"
              />
              {query && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full tabular-nums">
                  {hitCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Sticky Kategorie-Pills — minimalistisch, oben scrollen ═══ */}
      <div className="sticky top-[80px] z-30 bg-white/90 backdrop-blur-md border-b border-stone-100" data-testid="faq-cat-nav">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-3 -mx-1">
            {FAQS.map((c) => {
              const active = c.id === activeCat && !query.trim();
              return (
                <button
                  key={c.id}
                  onClick={() => scrollToCategory(c.id)}
                  className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-all ${
                    active
                      ? 'bg-stone-900 text-white'
                      : 'text-stone-500 hover:text-stone-900 hover:bg-stone-100'
                  }`}
                  data-testid={`faq-nav-${c.id}`}
                >
                  {c.cat}
                  <span className={`ml-1.5 text-[10px] tabular-nums ${active ? 'text-white/60' : 'text-stone-400'}`}>
                    {c.items.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ Content ═══ */}
      <section className="pb-20 sm:pb-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">

          {filteredFAQs.length === 0 && (
            <div className="mt-14 rounded-2xl border border-stone-200 bg-stone-50 p-8 text-center">
              <p className="text-sm text-stone-600 mb-4">
                Wir haben zu <strong>&bdquo;{query}&ldquo;</strong> keine passende Frage gefunden.
              </p>
              <Link to="/kontakt" className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-emerald-600 transition-colors">
                Frag uns direkt <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </Link>
            </div>
          )}

          {filteredFAQs.map((cat, ci) => (
            <div
              key={cat.id}
              id={cat.id}
              ref={(el) => (sectionRefs.current[cat.id] = el)}
              className="mt-16 first:mt-14 scroll-mt-32"
              data-testid={`faq-category-${cat.id}`}
            >
              {/* Kategorie-Header — minimalistisch */}
              <div className="mb-6 pb-4 border-b border-stone-200">
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="font-display text-[1.75rem] sm:text-[2rem] leading-tight tracking-[-0.025em] text-stone-900">
                    {cat.cat}
                  </h2>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-400 tabular-nums flex-shrink-0">
                    {String(ci + 1).padStart(2, '0')} / {String(filteredFAQs.length).padStart(2, '0')}
                  </span>
                </div>
                {cat.subtitle && (
                  <p className="text-[13.5px] text-stone-500 mt-2 leading-relaxed max-w-2xl">{cat.subtitle}</p>
                )}
              </div>

              {/* Items — pure Typo, keine Boxen */}
              <div>
                {cat.items.map((faq, fi) => {
                  const key = `${cat.id}-${fi}`;
                  return (
                    <FAQItem
                      key={key}
                      q={faq.q}
                      a={faq.a}
                      itemMax={faq.maxEur}
                      query={query}
                      open={openIdx === key}
                      toggle={() => setOpenIdx(openIdx === key ? '' : key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* ═══ Zwischen-CTA — verkaufspsychologisch: Reciprocity + urgency ═══ */}
          <div className="mt-20 rounded-3xl bg-stone-950 text-white p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="relative">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-300 mb-4">Rechne selbst</p>
              <h3 className="font-display text-2xl sm:text-4xl leading-[1.05] tracking-[-0.02em] max-w-xl">
                Deine Ersparnis. <span className="text-emerald-300 italic font-light">In 90 Sekunden.</span>
              </h3>
              <p className="text-[14px] text-stone-400 mt-4 max-w-md leading-relaxed">
                Schieb den Regler auf deine Jahres-Kilometer. Du siehst live, was du bekommst — bevor du zahlst.
              </p>
              <Link
                to="/#configurator"
                className="mt-8 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-6 py-3.5 rounded-full text-sm font-semibold transition-all"
              >
                Ersparnis berechnen <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        variant="light"
        headline="Noch eine Frage offen?"
        text="Wir antworten meist innerhalb von 24 h — auf Deutsch, ohne Warteschleife."
        buttonText="Kontakt aufnehmen"
        buttonTo="/kontakt"
      />
    </div>
  );
}
